<template>
  <div class="aq-page">

    <!-- Top Bar -->
    <div class="topbar">
      <div class="topbar-left">
        <v-btn icon class="back-btn" @click="$router.back()">
          <v-icon color="white" size="18">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="topbar-title">{{ isEditMode ? editQuoteNumber : 'New Quotation' }}</div>
          <div class="topbar-sub">
            <span v-if="isEditMode && currentRevision > 0">Rev {{ currentRevision }} · </span>
            <span v-if="isEditMode">Editing {{ editStatus }}</span>
            <span v-else>Draft — number assigned on save</span>
          </div>
        </div>
      </div>
      <div class="topbar-right">
        <span class="rep-label">{{ currentUser }}</span>

        <!-- PO Attach button (edit mode only) -->
        <template v-if="isEditMode">
          <input ref="poFileInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style="display:none" @change="onPoFileSelected" />
          <v-btn class="btn-action btn-po" @click="$refs.poFileInput.click()" :loading="poUploading">
            <v-icon left size="14">mdi-paperclip</v-icon>
            {{ poFile ? 'Replace PO' : 'Attach PO' }}
          </v-btn>
        </template>

        <v-btn v-if="isEditMode" class="btn-action btn-pdf" @click="downloadLatestPdf" :loading="pdfDownloading">
          <v-icon left size="14">mdi-download</v-icon>
          Download PDF
        </v-btn>

        <v-btn class="btn-send" :loading="saving" :disabled="!canSubmit" @click="submitQuote">
          <v-icon left size="14">mdi-send</v-icon>
          {{ isEditMode ? 'Save & Re-send' : 'Send Quote' }}
        </v-btn>
      </div>
    </div>

    <div class="page-content">

      <!-- PO Attachment display -->
      <div v-if="poFile" class="po-attached-bar">
        <div class="po-attached-left">
          <v-icon size="16" color="#4338ca">mdi-file-document-outline</v-icon>
          <span class="po-filename">{{ poFile.name }}</span>
          <span class="po-meta">Uploaded {{ poFile.uploadedAt }} by {{ poFile.uploadedBy }}</span>
        </div>
        <div class="po-attached-right">
          <a :href="poFile.url" target="_blank" class="po-link">View</a>
          <a :href="poFile.url" :download="poFile.name" class="po-link">Download</a>
        </div>
      </div>

      <!-- Revision history bar (edit mode) -->
      <div v-if="isEditMode && revisions.length" class="revision-bar">
        <v-icon size="13" color="#64748b">mdi-history</v-icon>
        <span class="rev-label">Revisions:</span>
        <span v-for="(r, i) in revisions" :key="i" class="rev-chip">
          Rev {{ r.revision }} · {{ r.modifiedBy }} · {{ formatRevDate(r.modifiedAt) }}
        </span>
      </div>

      <!-- Row 1: Customer + Quote Details -->
      <v-row>
        <v-col cols="12" md="5">
          <div class="section-label">Customer</div>
          <div class="card">
            <div v-if="selectedCustomer" class="customer-chip">
              <div class="cust-icon">
                <v-icon color="white" size="16">mdi-domain</v-icon>
              </div>
              <div class="cust-info">
                <div class="cust-name">{{ selectedCustomer['Company Name'] }}</div>
                <div class="cust-meta">
                  {{ selectedCustomer['Customer Account'] }}
                  <span v-if="selectedCustomer['Contact Person']"> · {{ selectedCustomer['Contact Person'] }}</span>
                  <span v-if="appliedDiscount > 0" class="disc-tag"> · {{ appliedDiscount }}% disc</span>
                </div>
              </div>
              <v-btn icon x-small @click="clearCustomer" :disabled="isEditMode">
                <v-icon size="16" color="#94a3b8">mdi-close</v-icon>
              </v-btn>
            </div>
            <div v-else style="position:relative">
              <div class="app-input">
                <v-icon size="16" color="#94a3b8">mdi-magnify</v-icon>
                <input
                  v-model="customerSearch"
                  placeholder="Search by name or account code…"
                  class="app-input-field"
                  @input="searchCustomers"
                />
              </div>
              <div v-if="showCustomerDrop && filteredCustomers.length" class="app-dropdown">
                <div v-for="c in filteredCustomers.slice(0,7)" :key="c.id"
                  class="drop-item" @click="selectCustomer(c)">
                  <div class="drop-name">{{ c['Company Name'] }}</div>
                  <div class="drop-meta">
                    {{ c['Customer Account'] }}
                    <span v-if="c['AutoDisc'] > 0" class="disc-tag"> · {{ c['AutoDisc'] }}% disc</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="7">
          <div class="section-label">Quote Details</div>
          <div class="card">
            <v-row dense>
              <v-col cols="12" sm="6">
                <div class="field-label">Customer Reference</div>
                <input v-model="customerReference" placeholder="e.g. PO-4521, Pump 3 seal…" class="app-input-standalone" />
              </v-col>
              <v-col cols="12" sm="6">
                <div class="field-label">Valid Until</div>
                <v-menu v-model="dateMenu" :close-on-content-click="false" offset-y>
                  <template v-slot:activator="{ on }">
                    <div class="app-input" v-on="on" style="cursor:pointer;margin-bottom:0">
                      <v-icon size="15" color="#94a3b8">mdi-calendar</v-icon>
                      <span :style="validUntil ? 'color:#1e293b' : 'color:#94a3b8'" style="font-size:13px">
                        {{ validUntil || 'Select date' }}
                      </span>
                    </div>
                  </template>
                  <v-date-picker v-model="validUntil" @input="dateMenu = false" />
                </v-menu>
              </v-col>
              <v-col cols="12">
                <div class="field-label">Notes to Customer</div>
                <textarea v-model="customerNotes"
                  placeholder="Any notes that will appear on the quote…"
                  class="app-textarea" rows="2" />
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <!-- Add Items -->
      <div class="section-label">Add Items</div>
      <div class="card" style="position:relative">
        <div style="display:flex;gap:10px;align-items:center">
          <div class="app-input" style="flex:1">
            <v-icon size="16" color="#94a3b8">mdi-magnify</v-icon>
            <input
              v-model="productSearch"
              placeholder="Search by product code, description or keywords…"
              class="app-input-field"
              @input="onProductSearch"
              @focus="showProductDrop = true"
            />
            <v-progress-circular v-if="searchingProducts" size="16" width="2" indeterminate color="#94a3b8" />
          </div>
          <v-btn class="btn-unlisted" @click="addFreeItem">
            <v-icon left size="13">mdi-text-box-plus-outline</v-icon>
            Add Unlisted
          </v-btn>
        </div>
        <div v-if="showProductDrop && (filteredProducts.length || (productSearch.length > 1 && !searchingProducts))" class="app-dropdown">
          <div v-if="!filteredProducts.length" style="padding:14px 16px;color:#94a3b8;font-size:13px">
            No products found
          </div>
          <div v-for="p in filteredProducts.slice(0,12)" :key="p.id"
            class="drop-item drop-item-product" @click="addProduct(p)">
            <div style="flex:1;min-width:0">
              <div style="display:flex;align-items:center;gap:5px;margin-bottom:3px;flex-wrap:wrap">
                <span class="code-pill">{{ p['Simple Code'] }}</span>
                <span v-if="p._isBom" class="mfr-pill">
                  <v-icon size="9" color="#1e3a5f" style="margin-right:2px">mdi-wrench</v-icon>Manufacture
                </span>
                <span v-if="p['Qty Available'] > 0" class="stock-pill">{{ p['Qty Available'] }} available</span>
                <span v-else class="nostock-pill">No stock</span>
                <span v-if="p._matchedOn === 'desc2'" class="match-pill">alt match</span>
              </div>
              <div style="font-size:11px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{{ p['Description'] }}</div>
            </div>
            <div style="text-align:right;flex-shrink:0;margin-left:12px">
              <div style="font-size:13px;font-weight:700;color:#1e293b">{{ fmt(p['Default Excl. Price']) }}</div>
              <div style="font-size:10px;color:#94a3b8">excl. VAT</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Line Items -->
      <div class="section-label">Line Items</div>
      <div class="tbl-card">
        <div class="tbl-head">
          <div class="th" style="width:28px">#</div>
          <div class="th" style="width:90px">Code</div>
          <div class="th" style="flex:1">Description</div>
          <div class="th" style="width:72px;text-align:center">Qty</div>
          <div class="th" style="width:105px;text-align:right">Unit Price</div>
          <div class="th" style="width:80px;text-align:center">Disc %</div>
          <div class="th" style="width:105px;text-align:right">Line Total</div>
          <div class="th" style="width:40px"></div>
        </div>

        <div v-for="(item, idx) in lineItems" :key="item._id"
          :class="['tbl-row', idx % 2 === 1 ? 'tbl-row-alt' : '']">

          <div style="width:28px;color:#cbd5e1;font-size:11px;padding-top:10px">{{ idx + 1 }}</div>

          <!-- Code column — editable for all types -->
          <div style="width:90px">
            <input v-model="item.code"
              :placeholder="item.type === 'freetext' ? 'Code' : ''"
              :readonly="item.type !== 'freetext'"
              class="tbl-input"
              :style="item.type !== 'freetext' ? 'background:#f1f5f9;color:#94a3b8;cursor:default' : ''"
            />
            <span v-if="item.type === 'freetext'" class="free-pill" style="margin-top:3px;display:inline-block">unlisted</span>
            <span v-if="item._isBom" class="mfr-pill" style="margin-top:3px;display:inline-flex">
              <v-icon size="9" color="#1e3a5f" style="margin-right:2px">mdi-wrench</v-icon>Mfr
            </span>
          </div>

          <!-- Description column -->
          <div style="flex:1;min-width:0">
            <input v-if="item.type === 'freetext'" v-model="item.description"
              placeholder="Describe the item…" class="tbl-input" style="width:100%" />
            <div v-else style="font-size:12px;color:#334155;line-height:1.4;padding:6px 0">{{ item.description }}</div>

            <!-- Stock/build info pills -->
            <div v-if="item._isBom && item._buildableQty !== null" style="margin-top:3px">
              <span :class="item.qty > item._buildableQty ? 'warn-pill' : 'avail-pill'">
                Can build: {{ item._buildableQty }}
              </span>
            </div>
            <div v-else-if="item._stockQty !== null" style="margin-top:3px">
              <span :class="item.qty > item._stockQty ? 'warn-pill' : 'avail-pill'">
                Stock: {{ item._stockQty }}
              </span>
            </div>

            <!-- Note -->
            <div v-if="!item.showNote && !item.note"
              @click="$set(item, 'showNote', true)"
              class="add-note-link">+ add note</div>
            <textarea v-if="item.showNote || item.note"
              v-model="item.note" placeholder="Note for this line item…"
              class="tbl-note" rows="1" />
          </div>

          <div style="width:72px;padding:0 4px">
            <input v-model.number="item.qty" type="number" min="1" step="1"
              class="tbl-input" style="text-align:center" @input="recalc(item); checkStock(item)" />
          </div>
          <div style="width:105px">
            <input v-model.number="item.unitPrice" type="number" min="0"
              class="tbl-input" style="text-align:right" @input="recalc(item)" />
          </div>
          <div style="width:80px;padding:0 4px">
            <input v-model.number="item.discount" type="number" min="0" max="100"
              class="tbl-input" style="text-align:center" @input="recalc(item)" />
          </div>

          <div style="width:105px;text-align:right;padding-top:8px">
            <div style="font-size:13px;font-weight:700;color:#1e293b">{{ fmt(item.lineTotal) }}</div>
            <div v-if="item.discount > 0" style="font-size:10px;color:#cbd5e1;text-decoration:line-through">{{ fmt(item.qty * item.unitPrice) }}</div>
          </div>

          <div style="width:40px;display:flex;justify-content:flex-end;padding-top:6px">
            <v-btn icon x-small @click="removeItem(idx)" style="color:#f87171">
              <v-icon size="14">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="!lineItems.length" class="tbl-empty">
          No items yet — search for a product above or add an unlisted item
        </div>

        <div v-if="lineItems.length" class="tbl-totals">
          <div class="tot-lines">
            <div class="tot-line">
              <span>Subtotal ({{ lineItems.length }} item{{ lineItems.length !== 1 ? 's' : '' }})</span>
              <span>{{ fmt(subtotal) }}</span>
            </div>
            <div v-if="totalDiscount > 0" class="tot-line" style="color:#059669;font-weight:600">
              <span>Total discount</span>
              <span>– {{ fmt(totalDiscount) }}</span>
            </div>
            <div class="tot-final">
              <span>Total excl. VAT</span>
              <span>{{ fmt(totalExcl) }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" max-width="480" persistent>
      <div class="success-dialog">
        <div class="sd-header">
          <v-icon color="white" size="28">mdi-check-circle-outline</v-icon>
          <div>
            <div class="sd-title">{{ isEditMode ? 'Quote Updated' : 'Quote Created' }}</div>
            <div class="sd-sub">{{ savedQuoteNumber }}</div>
          </div>
        </div>
        <div class="sd-body">
          <div class="sd-row">
            <v-icon size="15" color="#64748b">mdi-link-variant</v-icon>
            <span class="sd-label">Customer Portal</span>
          </div>
          <div class="sd-url">{{ savedPortalUrl }}</div>
          <div class="sd-actions">
            <v-btn class="sd-btn-primary" @click="downloadSavedPdf" :loading="pdfDownloading">
              <v-icon left size="14">mdi-download</v-icon>
              Download PDF
            </v-btn>
            <v-btn class="sd-btn-secondary" @click="copyPortalLink">
              <v-icon left size="14">mdi-content-copy</v-icon>
              Copy Link
            </v-btn>
          </div>
          <div v-if="copied" style="text-align:center;font-size:12px;color:#059669;margin-top:6px">Link copied!</div>
        </div>
        <div class="sd-footer">
          <v-btn text class="sd-btn-close" @click="goToList">Back to Quotations</v-btn>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" top right :timeout="4000">
      {{ snackMsg }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" small>Close</v-btn>
      </template>
    </v-snackbar>

  </div>
</template>

<script>
import { db, auth, storage } from '@/firebaseConfig'
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
  runTransaction, serverTimestamp, Timestamp,
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { generateQuotePdf, downloadPdfBlob } from '@/services/quotePdf'
import { uploadQuotePdf } from '@/services/quoteStorage'

const PORTAL_BASE = 'nexus-quotes.com/za'

export default {
  name: 'AddQuotation',

  props: {
    // When editing an existing quote, pass its Firestore doc id via route param
    quoteId: { type: String, default: null },
  },

  data: () => ({
    // Edit mode state
    isEditMode: false,
    editQuoteDocId: null,
    editQuoteNumber: '',
    editStatus: '',
    currentRevision: 0,
    revisions: [],

    currentUser: '', currentUserUid: '', currentUserEmail: '',
    customerReference: '', validUntil: '', customerNotes: '', dateMenu: false,

    customerSearch: '', customers: [], filteredCustomers: [],
    selectedCustomer: null, showCustomerDrop: false, appliedDiscount: 0,

    productSearch: '', allInventory: [], boms: {},
    filteredProducts: [], showProductDrop: false, searchingProducts: false,

    lineItems: [],
    saving: false,

    // PO attachment
    poFile: null,
    poUploading: false,

    // PDF
    pdfDownloading: false,
    savedPdfBlob: null,

    successDialog: false,
    savedQuoteNumber: '',
    savedPortalUrl: '',
    copied: false,

    snackbar: false, snackMsg: '', snackColor: 'success',
  }),

  computed: {
    subtotal()      { return this.lineItems.reduce((s, i) => s + (i.qty || 0) * (i.unitPrice || 0), 0) },
    totalDiscount() { return this.lineItems.reduce((s, i) => s + ((i.qty || 0) * (i.unitPrice || 0) - i.lineTotal), 0) },
    totalExcl()     { return this.lineItems.reduce((s, i) => s + i.lineTotal, 0) },
    canSubmit()     { return this.selectedCustomer && this.lineItems.length > 0 },
  },

  async created() {
    await this.loadUser()
    await this.loadCustomers()
    await this.loadInventory()
    await this.loadBoms()

    // Check if we're editing an existing quote
    const routeId = this.$route?.params?.id || this.quoteId
    if (routeId) {
      await this.loadExistingQuote(routeId)
    } else {
      const d = new Date(); d.setDate(d.getDate() + 90)
      this.validUntil = d.toISOString().substr(0, 10)
    }
  },

  methods: {
    // ─── Load existing quote for editing ───────────────────────────────────
    async loadExistingQuote(docId) {
      try {
        const snap = await getDoc(doc(db, 'quotes', docId))
        if (!snap.exists()) { this.toast('Quote not found', 'error'); return }
        const data = snap.data()

        this.isEditMode       = true
        this.editQuoteDocId   = docId
        this.editQuoteNumber  = data.quoteNumber
        this.editStatus       = data.status
        this.currentRevision  = data.currentRevision || 0
        this.revisions        = data.revisions || []
        this.customerReference = data.customerReference || ''
        this.validUntil        = data.validUntil || ''
        this.customerNotes     = data.customerNotes || ''
        this.poFile            = data.poFile || null

        // Restore customer — find from loaded list
        if (data.customerId) {
          const found = this.customers.find(c => c.id === data.customerId)
          if (found) { this.selectedCustomer = found; this.appliedDiscount = found['AutoDisc'] || 0 }
        }
        if (!this.selectedCustomer) {
          // Reconstruct minimal customer object from saved data
          this.selectedCustomer = {
            id: data.customerId,
            'Company Name': data.customerName,
            'Customer Account': data.accountCode,
            'Contact Person': data.contactPerson,
            'EMail': data.contactEmail,
            'AccountTerms': data.accountTerms,
            'AutoDisc': 0,
          }
        }

        // Restore line items
        this.lineItems = (data.lineItems || []).map(item => ({
          _id: Date.now() + Math.random(),
          type:        item.type || 'standard',
          code:        item.code || '',
          description: item.description || '',
          group:       item.group || '',
          productId:   item.productId || null,
          qty:         item.qty,
          unitPrice:   item.unitPrice,
          discount:    item.discount,
          lineTotal:   item.lineTotal,
          note:        item.note || '',
          showNote:    !!item.note,
          approved:    item.approved ?? null,
          _isBom:      item.isBom || false,
          _stockQty:   item.stockQtyAtQuote ?? null,
          _buildableQty: item.buildableQtyAtQuote ?? null,
        }))
      } catch (e) {
        console.error('loadExistingQuote', e)
        this.toast('Failed to load quote', 'error')
      }
    },

    // ─── User / data loading ────────────────────────────────────────────────
    async loadUser() {
      const u = auth.currentUser; if (!u) return
      this.currentUserUid = u.uid; this.currentUserEmail = u.email
      try {
        const d = await getDoc(doc(db, 'users', u.uid))
        this.currentUser = d.exists() ? (d.data().displayName || u.email) : u.email
      } catch { this.currentUser = u.email }
    },

    async loadCustomers() {
      try {
        const s = await getDocs(collection(db, 'customers'))
        this.customers = s.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) { console.error('loadCustomers', e) }
    },

    async loadInventory() {
      try {
        const s = await getDocs(collection(db, 'Evolution Inventory'))
        this.allInventory = s.docs
          .map(d => {
            const data = d.data()
            
            // 1. Check for both casing variations and strictly check for true (boolean or string)
            const isDel = 
              data.IsDeleted === true || data.IsDeleted === 'true' || 
              data.isDeleted === true || data.isDeleted === 'true'
              
            return { 
              id: d.id, 
              ...data, 
              _isDeleted: isDel // Tag it so we can filter it
            }
          })
          .filter(p => !p._isDeleted) // 2. Remove the deleted items before they hit the dropdown
          
      } catch (e) { 
        console.error('loadInventory', e) 
      }
    },

    async loadBoms() {
      try {
        const s = await getDocs(collection(db, 'BOMRecipes'))
        s.docs.forEach(d => {
          const data = d.data()
          this.boms[data.BillCode || d.id] = data
        })
      } catch (e) { console.error('loadBoms', e) }
    },

    async reserveQuoteNumber() {
      const year = new Date().getFullYear()
      const ref  = doc(db, 'quoteCounters', String(year))
      const num  = await runTransaction(db, async t => {
        const snap = await t.get(ref)
        const n    = snap.exists() ? snap.data().last + 1 : 1
        t.set(ref, { last: n }, { merge: true })
        return n
      })
      return `QUO-${year}-${String(num).padStart(5, '0')}`
    },

    // ─── Customer search ────────────────────────────────────────────────────
    searchCustomers() {
      const q = (this.customerSearch || '').toLowerCase()
      this.filteredCustomers = q.length < 2 ? [] : this.customers.filter(c =>
        c['Company Name']?.toLowerCase().includes(q) || c['Customer Account']?.toLowerCase().includes(q)
      )
      this.showCustomerDrop = this.filteredCustomers.length > 0
    },

    selectCustomer(c) {
      this.selectedCustomer = c
      this.showCustomerDrop = false
      this.customerSearch   = ''
      this.appliedDiscount  = c['AutoDisc'] || 0
      this.lineItems.forEach(i => { i.discount = this.appliedDiscount; this.recalc(i) })
    },

    clearCustomer() { this.selectedCustomer = null; this.appliedDiscount = 0 },

    // ─── Product search (feature 3: search desc2 + keywords) ────────────────
    onProductSearch() {
      const q = (this.productSearch || '').toLowerCase().trim()
      if (q.length < 2) { this.filteredProducts = []; this.showProductDrop = false; return }
      this.searchingProducts = true
      this.showProductDrop   = true

      this.filteredProducts = this.allInventory
        .map(p => {
          const code     = (p['Simple Code']      || '').toLowerCase()
          const desc     = (p['Description']       || '').toLowerCase()
          const desc2    = (p['Description 2']     || '').toLowerCase()
          const keywords = (p['Keywords']          || '').toLowerCase()
          const altDesc  = (p['Alt Description']   || '').toLowerCase()

          const matchCode  = code.includes(q)
          const matchDesc  = desc.includes(q)
          const matchDesc2 = desc2.includes(q)
          const matchKw    = keywords.includes(q)
          const matchAlt   = altDesc.includes(q)

          if (!matchCode && !matchDesc && !matchDesc2 && !matchKw && !matchAlt) return null

          let score = 3
          let matchedOn = 'desc'
          if (code.startsWith(q))         { score = 0; matchedOn = 'code' }
          else if (code.includes(q))      { score = 1; matchedOn = 'code' }
          else if (desc.includes(q))      { score = 2; matchedOn = 'desc' }
          else if (desc2.includes(q))     { score = 3; matchedOn = 'desc2' }
          else if (keywords.includes(q))  { score = 3; matchedOn = 'keyword' }
          else if (altDesc.includes(q))   { score = 3; matchedOn = 'alt' }

          return { ...p, _isBom: !!this.boms[p['Simple Code']], _score: score, _matchedOn: matchedOn }
        })
        .filter(Boolean)
        .sort((a, b) => a._score - b._score || (a['Simple Code'] || '').localeCompare(b['Simple Code'] || ''))

      this.searchingProducts = false
    },

    async addProduct(p) {
      const code       = p['Simple Code']
      const isBom      = !!this.boms[code]
      const price      = this.getPriceForCustomer(p)
      const stockQty   = p['Qty Available'] || 0
      let buildableQty = null
      if (isBom) buildableQty = await this.calcBuildableQty(code)

      const item = {
        _id: Date.now() + Math.random(),
        type: 'standard', code,
        description: p['Description'],
        group: p['Group'] || '',
        productId: p.id,
        qty: 1, unitPrice: price,
        discount: this.appliedDiscount, lineTotal: 0,
        note: '', showNote: false, approved: null,
        _isBom: isBom,
        _stockQty: stockQty,
        _buildableQty: isBom ? buildableQty : null,
      }
      this.recalc(item)
      this.lineItems.push(item)
      this.productSearch = ''; this.filteredProducts = []; this.showProductDrop = false
    },

    // Feature 2: unlisted item now has same layout as stock item
    addFreeItem() {
      this.lineItems.push({
        _id: Date.now() + Math.random(),
        type: 'freetext', code: '', description: '',
        productId: null, qty: 1, unitPrice: 0,
        discount: this.appliedDiscount, lineTotal: 0,
        note: '', showNote: false, approved: null,
        _isBom: false, _stockQty: null, _buildableQty: null,
      })
      this.productSearch = ''; this.filteredProducts = []; this.showProductDrop = false
    },

    async calcBuildableQty(billCode) {
      const bom = this.boms[billCode]
      if (!bom?.Components?.length) return null
      const stockMap = {}
      this.allInventory.forEach(p => { stockMap[p['Simple Code']] = p['Qty Available'] || 0 })
      let max = Infinity
      for (const c of bom.Components) {
        max = Math.min(max, Math.floor((stockMap[c.ItemCode] || 0) / (c.QtyRequired || 1)))
      }
      return max === Infinity ? 0 : max
    },

    async checkStock(item) {
      if (item.type === 'freetext' || !item.code) return
      if (item._isBom) item._buildableQty = await this.calcBuildableQty(item.code)
    },

    getPriceForCustomer(p) {
      if (this.selectedCustomer?.pricelistCode) {
        const k = this.selectedCustomer.pricelistCode
        if (p.pricelists?.[k]) return p.pricelists[k]
      }
      return p['Default Excl. Price'] || 0
    },

    recalc(item) {
      const gross = (item.qty || 0) * (item.unitPrice || 0)
      const disc  = Math.min(Math.max(item.discount || 0, 0), 100)
      item.lineTotal = gross * (1 - disc / 100)
    },

    removeItem(idx) { this.lineItems.splice(idx, 1) },

    // ─── Feature 1: PO Attachment ──────────────────────────────────────────
    async onPoFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      this.poUploading = true
      try {
        const path    = `purchase-orders/${this.editQuoteNumber}/${Date.now()}_${file.name}`
        const fileRef = storageRef(storage, path)
        await uploadBytes(fileRef, file)
        const url = await getDownloadURL(fileRef)
        this.poFile = {
          name:       file.name,
          url,
          path,
          uploadedBy: this.currentUser,
          uploadedAt: new Date().toISOString().slice(0, 10),
        }
        if (this.editQuoteDocId) {
          await updateDoc(doc(db, 'quotes', this.editQuoteDocId), { poFile: this.poFile })
        }
        this.toast('PO attached successfully', 'success')
      } catch (e) {
        console.error('PO upload failed', e)
        this.toast('Failed to upload PO', 'error')
      } finally {
        this.poUploading = false
        this.$refs.poFileInput.value = ''
      }
    },

    // ─── Feature 5: Download latest PDF from Firestore live data ───────────
    async downloadLatestPdf() {
      if (!this.editQuoteDocId) return
      this.pdfDownloading = true
      try {
        // Always fetch fresh from Firestore
        const snap = await getDoc(doc(db, 'quotes', this.editQuoteDocId))
        if (!snap.exists()) { this.toast('Quote not found', 'error'); return }
        const liveData = snap.data()

        const blob = await generateQuotePdf({
          ...liveData,
          lineItems: liveData.lineItems,
        })
        downloadPdfBlob(blob, `${liveData.quoteNumber}.pdf`)
      } catch (e) {
        console.error('downloadLatestPdf', e)
        this.toast('Failed to generate PDF', 'error')
      } finally {
        this.pdfDownloading = false
      }
    },

    // ─── Submit (create or update) ─────────────────────────────────────────
    async submitQuote() {
      if (!this.canSubmit) return
      this.saving = true
      try {
        if (this.isEditMode) {
          await this.updateExistingQuote()
        } else {
          await this.createNewQuote()
        }
      } catch (e) {
        console.error('submitQuote failed:', e)
        this.toast('Failed to save quote — check console', 'error')
      } finally {
        this.saving = false
      }
    },

    async createNewQuote() {
      const quoteNumber  = await this.reserveQuoteNumber()
      const portalUrl    = `${PORTAL_BASE}/${quoteNumber}`
      const now          = serverTimestamp()
      const expiresAt    = Timestamp.fromDate(new Date(this.validUntil + 'T23:59:59'))
      const accountTerms = this.selectedCustomer['AccountTerms'] ?? 0

      const lineItemsForDb = this.buildLineItemsForDb()

      const quoteData = {
        quoteNumber,
        status:            'sent',
        accountTerms,
        customerId:        this.selectedCustomer.id,
        customerName:      this.selectedCustomer['Company Name'],
        accountCode:       this.selectedCustomer['Customer Account'],
        contactPerson:     this.selectedCustomer['Contact Person'] || '',
        contactEmail:      this.selectedCustomer['EMail']          || '',
        customerReference: this.customerReference,
        validUntil:        this.validUntil,
        customerNotes:     this.customerNotes,
        lineItems:         lineItemsForDb,
        subtotal:          this.subtotal,
        totalDiscount:     this.totalDiscount,
        totalExcl:         this.totalExcl,
        lineCount:         this.lineItems.length,
        createdBy:         this.currentUser,
        createdByUid:      this.currentUserUid,
        createdByEmail:    this.currentUserEmail,
        createdAt:         now,
        updatedAt:         now,
        portalUrl,
        portalAccessCode:  this.selectedCustomer['Customer Account'],
        portalExpiresAt:   expiresAt,
        portalViewed:      false,
        pdfUrl:            '',
        pdfGeneratedAt:    null,
        customerPO:        '',
        customerComment:   '',
        customerAccepted:  null,
        customerAcceptedAt: null,
        erpExported:       false,
        erpReference:      '',
        isArchived:        false,
        poFile:            null,
        // Feature 6: revision tracking
        currentRevision:   1,
        revisions: [{
          revision:    1,
          modifiedBy:  this.currentUser,
          modifiedByUid: this.currentUserUid,
          modifiedAt:  new Date().toISOString(),
          note:        'Quote created',
        }],
        statusHistory: [{
          status:       'sent',
          changedBy:    this.currentUser,
          changedByUid: this.currentUserUid,
          timestamp:    new Date().toISOString(),
          note:         'Quote created',
        }],
      }

      const docRef = await addDoc(collection(db, 'quotes'), quoteData)

      const pdfBlob = await generateQuotePdf({
        ...quoteData, quoteNumber, portalUrl, accountTerms, lineItems: lineItemsForDb,
      })
      const pdfUrl = await uploadQuotePdf(quoteNumber, pdfBlob)
      await updateDoc(doc(db, 'quotes', docRef.id), { pdfUrl, pdfGeneratedAt: serverTimestamp() })

      await addDoc(collection(db, 'auditLog'), {
        entityType: 'quote', entityId: docRef.id, quoteNumber,
        action: 'CREATED', performedBy: this.currentUser,
        performedByUid: this.currentUserUid, timestamp: now,
      })

      this.savedQuoteNumber = quoteNumber
      this.savedPortalUrl   = portalUrl
      this.savedPdfBlob     = pdfBlob
      this.successDialog    = true
    },

    // Feature 4: Update existing quote (editable sent quotes)
    async updateExistingQuote() {
      const now            = serverTimestamp()
      const newRevision    = (this.currentRevision || 1) + 1
      const lineItemsForDb = this.buildLineItemsForDb()

      const revisionEntry = {
        revision:     newRevision,
        modifiedBy:   this.currentUser,
        modifiedByUid: this.currentUserUid,
        modifiedAt:   new Date().toISOString(),
        note:         'Quote updated',
      }

      const updateData = {
        customerReference: this.customerReference,
        validUntil:        this.validUntil,
        customerNotes:     this.customerNotes,
        lineItems:         lineItemsForDb,
        subtotal:          this.subtotal,
        totalDiscount:     this.totalDiscount,
        totalExcl:         this.totalExcl,
        lineCount:         this.lineItems.length,
        updatedAt:         now,
        currentRevision:   newRevision,
        revisions:         [...this.revisions, revisionEntry],
        status:            'sent', // re-send
        statusHistory: [
          ...(this._originalStatusHistory || []),
          {
            status:       'sent',
            changedBy:    this.currentUser,
            changedByUid: this.currentUserUid,
            timestamp:    new Date().toISOString(),
            note:         `Quote updated — Rev ${newRevision}`,
          },
        ],
      }

      await updateDoc(doc(db, 'quotes', this.editQuoteDocId), updateData)

      // Feature 5: regenerate PDF from live data after update
      const freshSnap = await getDoc(doc(db, 'quotes', this.editQuoteDocId))
      const liveData  = freshSnap.data()
      const pdfBlob   = await generateQuotePdf(liveData)
      const pdfUrl    = await uploadQuotePdf(this.editQuoteNumber, pdfBlob)
      await updateDoc(doc(db, 'quotes', this.editQuoteDocId), { pdfUrl, pdfGeneratedAt: serverTimestamp() })

      await addDoc(collection(db, 'auditLog'), {
        entityType: 'quote', entityId: this.editQuoteDocId,
        quoteNumber: this.editQuoteNumber,
        action: 'UPDATED', revision: newRevision,
        performedBy: this.currentUser,
        performedByUid: this.currentUserUid,
        timestamp: now,
      })

      this.savedQuoteNumber = this.editQuoteNumber
      this.savedPortalUrl   = liveData.portalUrl
      this.savedPdfBlob     = pdfBlob
      this.successDialog    = true
    },

    buildLineItemsForDb() {
      return this.lineItems.map((item, idx) => ({
        lineNumber:          idx + 1,
        type:                item.type,
        code:                item.code || '',
        description:         item.description,
        group:               item.group || '',
        productId:           item.productId || null,
        isBom:               item._isBom || false,
        qty:                 item.qty,
        unitPrice:           item.unitPrice,
        discount:            item.discount,
        lineTotal:           item.lineTotal,
        note:                item.note || '',
        approved:            item.approved ?? null,
        stockQtyAtQuote:     item._isBom ? null : (item._stockQty ?? 0),
        buildableQtyAtQuote: item._isBom ? (item._buildableQty ?? 0) : null,
      }))
    },

    downloadSavedPdf() {
      if (!this.savedPdfBlob) return
      this.pdfDownloading = true
      downloadPdfBlob(this.savedPdfBlob, `${this.savedQuoteNumber}.pdf`)
      setTimeout(() => { this.pdfDownloading = false }, 800)
    },

    async copyPortalLink() {
      try {
        await navigator.clipboard.writeText(this.savedPortalUrl)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2500)
      } catch {
        this.toast('Could not copy — please copy manually', 'warning')
      }
    },

    goToList() {
      this.successDialog = false
      this.$router.push('/AllQuotation')
    },

    formatRevDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    fmt(v) {
      return 'R ' + Number(v || 0).toLocaleString('en-ZA', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },

    toast(msg, color = 'success') {
      this.snackMsg = msg; this.snackColor = color; this.snackbar = true
    },
  },
}
</script>

<style scoped>
.aq-page { background: #f1f5f9; min-height: 100vh; font-family: 'Inter', sans-serif; }

/* Topbar */
.topbar { background: #1e2d3d; padding: 14px 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.topbar-left { display: flex; align-items: center; gap: 12px; }
.back-btn { background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.2) !important; border-radius: 50% !important; width: 34px !important; height: 34px !important; transition: background .15s !important; }
.back-btn:hover { background: rgba(255,255,255,0.2) !important; }
.topbar-title { color: #fff; font-size: 18px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.topbar-sub { color: rgba(255,255,255,0.45); font-size: 11px; font-family: monospace; margin-top: 2px; }
.topbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.rep-label { color: rgba(255,255,255,0.45); font-size: 12px; }

.btn-action { border-radius: 8px !important; font-size: 12px !important; font-weight: 700 !important; text-transform: none !important; letter-spacing: 0 !important; height: 36px !important; padding: 0 14px !important; }
.btn-po  { background: rgba(255,255,255,0.12) !important; color: #fff !important; border: 1px solid rgba(255,255,255,0.25) !important; }
.btn-po:hover { background: rgba(255,255,255,0.2) !important; }
.btn-pdf { background: rgba(255,255,255,0.12) !important; color: #fff !important; border: 1px solid rgba(255,255,255,0.25) !important; }
.btn-pdf:hover { background: rgba(255,255,255,0.2) !important; }
.btn-send { background: #fff !important; color: #1e2d3d !important; border-radius: 8px !important; font-size: 12px !important; font-weight: 700 !important; text-transform: none !important; letter-spacing: 0 !important; height: 36px !important; padding: 0 16px !important; }
.btn-send:hover { background: #e0e7ff !important; color: #3730a3 !important; }
.btn-unlisted { background: #f1f5f9 !important; color: #475569 !important; border: 1.5px solid #e2e8f0 !important; border-radius: 8px !important; font-size: 12px !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: 0 !important; height: 38px !important; padding: 0 14px !important; white-space: nowrap; }
.btn-unlisted:hover { background: #e2e8f0 !important; }

/* PO attached bar */
.po-attached-bar { background: #eef2ff; border: 1.5px solid #c7d2fe; border-radius: 10px; padding: 10px 16px; display: flex; align-items: center; justify-content: space-between; margin: 0 24px 12px; flex-wrap: wrap; gap: 8px; }
.po-attached-left { display: flex; align-items: center; gap: 8px; }
.po-filename { font-size: 12px; font-weight: 700; color: #4338ca; }
.po-meta { font-size: 11px; color: #94a3b8; }
.po-attached-right { display: flex; gap: 12px; }
.po-link { font-size: 12px; font-weight: 600; color: #4338ca; text-decoration: none; }
.po-link:hover { text-decoration: underline; }

/* Revision bar */
.revision-bar { background: #fefce8; border: 1px solid #fde68a; border-radius: 8px; padding: 8px 16px; margin: 0 24px 12px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rev-label { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #92400e; }
.rev-chip { font-size: 11px; color: #78350f; background: #fef3c7; border: 1px solid #fde68a; border-radius: 5px; padding: 2px 8px; }

/* Content */
.page-content { padding: 20px 24px 48px; max-width: 1200px; margin: 0 auto; }
.section-label { font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: #64748b; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
.section-label::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
.field-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #64748b; margin-bottom: 5px; }
.card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 18px; margin-bottom: 18px; }

/* Customer chip */
.customer-chip { background: #f0f4ff; border: 1.5px solid #c7d2fe; border-radius: 10px; padding: 11px 14px; display: flex; align-items: center; gap: 11px; }
.cust-icon { width: 34px; height: 34px; background: #1e2d3d; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cust-info { flex: 1; min-width: 0; }
.cust-name { font-size: 13px; font-weight: 700; color: #1e293b; }
.cust-meta { font-size: 11px; color: #64748b; margin-top: 2px; }
.disc-tag { color: #059669; font-weight: 600; }

/* Inputs */
.app-input { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; display: flex; align-items: center; gap: 7px; transition: border-color .15s; margin-bottom: 0; }
.app-input:hover { border-color: #94a3b8; }
.app-input-field { background: none; border: none; outline: none; font-size: 13px; color: #1e293b; flex: 1; font-family: inherit; }
.app-input-field::placeholder { color: #94a3b8; }
.app-input-standalone { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #1e293b; width: 100%; font-family: inherit; outline: none; transition: border-color .15s; }
.app-input-standalone:hover, .app-input-standalone:focus { border-color: #94a3b8; }
.app-input-standalone::placeholder { color: #94a3b8; }
.app-textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #1e293b; width: 100%; font-family: inherit; outline: none; resize: none; transition: border-color .15s; }
.app-textarea:hover, .app-textarea:focus { border-color: #94a3b8; }
.app-textarea::placeholder { color: #94a3b8; }

/* Dropdown */
.app-dropdown { position: absolute; left: 0; right: 0; top: calc(100% + 4px); background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.1); z-index: 200; max-height: 320px; overflow-y: auto; }
.drop-item { padding: 10px 14px; cursor: pointer; border-bottom: 1px solid #f8fafc; transition: background .1s; }
.drop-item:hover { background: #f0f4ff; }
.drop-item:last-child { border-bottom: none; }
.drop-item-product { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.drop-name { font-size: 13px; font-weight: 600; color: #1e293b; }
.drop-meta { font-size: 11px; color: #64748b; margin-top: 2px; }

/* Pills */
.code-pill   { font-family: monospace; font-size: 10px; color: #4338ca; background: #eef2ff; padding: 2px 6px; border-radius: 4px; }
.mfr-pill    { font-size: 10px; color: #1e3a5f; background: #dbeafe; border: 1px solid #93c5fd; padding: 2px 7px; border-radius: 4px; font-weight: 700; display: inline-flex; align-items: center; }
.stock-pill  { font-size: 10px; color: #059669; background: #ecfdf5; padding: 2px 6px; border-radius: 4px; }
.nostock-pill{ font-size: 10px; color: #94a3b8; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; }
.free-pill   { font-size: 10px; color: #b45309; background: #fffbeb; padding: 2px 6px; border-radius: 4px; }
.avail-pill  { font-size: 10px; color: #059669; background: #ecfdf5; padding: 2px 6px; border-radius: 4px; font-weight: 600; }
.warn-pill   { font-size: 10px; color: #b91c1c; background: #fee2e2; padding: 2px 6px; border-radius: 4px; font-weight: 600; }
.match-pill  { font-size: 10px; color: #6d28d9; background: #ede9fe; padding: 2px 6px; border-radius: 4px; }

/* Table */
.tbl-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin-bottom: 18px; }
.tbl-head { display: flex; gap: 8px; padding: 9px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.th { font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #94a3b8; }
.tbl-row { display: flex; gap: 8px; padding: 12px 16px; border-bottom: 1px solid #f8fafc; align-items: flex-start; transition: background .1s; }
.tbl-row-alt { background: #fafafa; }
.tbl-row:hover { background: #f0f4ff; }
.tbl-input { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 5px 8px; font-size: 12px; color: #1e293b; width: 100%; outline: none; font-family: inherit; transition: border-color .15s; }
.tbl-input:focus { border-color: #6366f1; }
.tbl-note { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 5px 8px; font-size: 11px; color: #64748b; width: 100%; outline: none; resize: none; font-family: inherit; margin-top: 5px; }
.add-note-link { font-size: 10px; color: #94a3b8; cursor: pointer; margin-top: 5px; display: inline-block; }
.add-note-link:hover { color: #64748b; }
.tbl-empty { padding: 32px; text-align: center; color: #94a3b8; font-size: 13px; }
.tbl-totals { display: flex; justify-content: flex-end; padding: 14px 16px; background: #f8fafc; border-top: 1px solid #e2e8f0; }
.tot-lines { min-width: 260px; }
.tot-line { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 12px; color: #64748b; }
.tot-final { display: flex; justify-content: space-between; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 14px; font-weight: 800; color: #1e293b; }

/* Success Dialog */
.success-dialog { background: #fff; border-radius: 16px; overflow: hidden; }
.sd-header { background: #1e2d3d; padding: 20px 24px; display: flex; align-items: center; gap: 14px; }
.sd-title { color: #fff; font-size: 16px; font-weight: 800; }
.sd-sub { color: rgba(255,255,255,0.5); font-size: 12px; font-family: monospace; margin-top: 2px; }
.sd-body { padding: 24px; }
.sd-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.sd-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #64748b; }
.sd-url { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; font-family: monospace; font-size: 12px; color: #4338ca; word-break: break-all; margin-bottom: 18px; }
.sd-actions { display: flex; gap: 10px; }
.sd-btn-primary { background: #1e2d3d !important; color: #fff !important; border-radius: 8px !important; font-weight: 700 !important; text-transform: none !important; letter-spacing: 0 !important; height: 38px !important; flex: 1; }
.sd-btn-secondary { background: #f1f5f9 !important; color: #1e293b !important; border-radius: 8px !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: 0 !important; height: 38px !important; flex: 1; }
.sd-footer { border-top: 1px solid #f1f5f9; padding: 12px 24px; display: flex; justify-content: flex-end; }
.sd-btn-close { color: #64748b !important; font-size: 12px !important; text-transform: none !important; letter-spacing: 0 !important; }
</style>